import { forwardRef, useEffect, useMemo, useState } from "react";
import { getDownloadURL, ref } from "firebase/storage";
import type { FirebaseStorage } from "firebase/storage";
import clsx from "clsx";

const FIREBASE_STORAGE_HOSTS = [
  "firebasestorage.googleapis.com",
  "storage.googleapis.com",
  "storage.cloud.google.com",
];

export const CLIENT_LOGO_VARIANTS = [32, 64, 128, 512] as const;
export type ClientLogoVariant = (typeof CLIENT_LOGO_VARIANTS)[number];
const DEFAULT_LOGO_VARIANT: ClientLogoVariant = 32;
const LOGO_FILE_EXTENSION = ".png";
const VARIANT_SUFFIX_PATTERN = /_(\d+)x\1$/;

let firebaseStorageCache: FirebaseStorage | null = null;

const getFirebaseStorage = async (): Promise<FirebaseStorage | null> => {
  if (firebaseStorageCache) {
    return firebaseStorageCache;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    const module = await import("@/config/firebase");
    firebaseStorageCache = module.storage;
    return firebaseStorageCache;
  } catch (error) {
    console.warn("Não foi possível carregar o storage do Firebase:", error);
    return null;
  }
};

type ClientLogoClient =
  | {
      id?: string | null;
      name?: string | null;
      logoUrl?: string | null;
    }
  | null;

export interface ClientLogoProps {
  client?: ClientLogoClient;
  clientId?: string | null;
  logoUrl?: string | null;
  variant?: ClientLogoVariant;
  className?: string;
}

const downloadUrlCache = new Map<string, string | null>();

const splitFirst = (value: string, separator: string): [string, string | null] => {
  const index = value.indexOf(separator);
  if (index === -1) {
    return [value, null];
  }
  return [value.slice(0, index), value.slice(index + separator.length)];
};

const shouldAppendSuffix = (url: string): boolean => {
  if (url.startsWith("gs://")) {
    return true;
  }

  if (url.startsWith("/")) {
    return true;
  }

  try {
    const parsed = new URL(url);
    if (!["https:", "http:"].includes(parsed.protocol)) {
      return false;
    }

    return FIREBASE_STORAGE_HOSTS.some(
      (host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)
    );
  } catch {
    return true;
  }
};

const buildVariantSuffix = (variant: ClientLogoVariant): string => `_${variant}x${variant}`;

const stripVariantSuffix = (value: string): string => value.replace(VARIANT_SUFFIX_PATTERN, "");

const appendSuffixToBasePath = (base: string, suffix: string): string => {
  const lastSlashIndex = base.lastIndexOf("/");
  const lastDotIndex = base.lastIndexOf(".");

  if (lastDotIndex === -1 || lastDotIndex < lastSlashIndex) {
    const baseWithoutVariant = stripVariantSuffix(base);
    return `${baseWithoutVariant}${suffix}${LOGO_FILE_EXTENSION}`;
  }

  const name = base.slice(0, lastDotIndex);
  const extension = base.slice(lastDotIndex);
  const normalizedName = stripVariantSuffix(name);
  return `${normalizedName}${suffix}${extension}`;
};

const rebuildUrlWithSuffix = (url: string, suffix: string): string => {
  const [urlWithoutHash, hashPart] = splitFirst(url, "#");
  const [basePath, queryPart] = splitFirst(urlWithoutHash, "?");

  if (basePath.includes(suffix)) {
    return url;
  }

  const suffixedBase = appendSuffixToBasePath(basePath, suffix);

  let rebuilt = suffixedBase;
  if (queryPart) {
    rebuilt += `?${queryPart}`;
  }
  if (hashPart) {
    rebuilt += `#${hashPart}`;
  }

  return rebuilt;
};

const getClientInitials = (name?: string | null) => {
  if (!name) {
    return "CL";
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    return "CL";
  }

  const segments = trimmedName.split(/\s+/);
  if (segments.length === 1) {
    return segments[0].slice(0, 2).toUpperCase();
  }

  return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
};

const normalizeVariant = (variant?: ClientLogoVariant): ClientLogoVariant => {
  if (!variant) {
    return DEFAULT_LOGO_VARIANT;
  }
  if (CLIENT_LOGO_VARIANTS.includes(variant)) {
    return variant;
  }
  return DEFAULT_LOGO_VARIANT;
};

export const buildClientLogoStoragePath = (
  clientId: string,
  variant?: ClientLogoVariant
): string => {
  const targetVariant = normalizeVariant(variant);
  const suffix = buildVariantSuffix(targetVariant);
  return `clients/${clientId}/logo/${clientId}${suffix}${LOGO_FILE_EXTENSION}`;
};

export const buildClientLogoUrl = (
  url?: string | null,
  variant?: ClientLogoVariant
): string | null => {
  if (!url) {
    return null;
  }

  const targetVariant = normalizeVariant(variant);
  const suffix = buildVariantSuffix(targetVariant);

  if (!shouldAppendSuffix(url)) {
    return url;
  }

  return rebuildUrlWithSuffix(url, suffix);
};

const resolveClientLogoId = (
  client: ClientLogoClient,
  explicitClientId?: string | null
): string | null => {
  if (explicitClientId) {
    return explicitClientId;
  }
  return client?.id ?? null;
};

const useClientLogoSource = ({
  client,
  clientId,
  variant,
}: {
  client: ClientLogoClient;
  clientId?: string | null;
  variant: ClientLogoVariant;
}): string | null => {
  const resolvedClientId = resolveClientLogoId(client, clientId);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!resolvedClientId) {
      setResolvedUrl(null);
      return;
    }

    const cacheKey = `${resolvedClientId}_${variant}`;
    if (downloadUrlCache.has(cacheKey)) {
      setResolvedUrl(downloadUrlCache.get(cacheKey) ?? null);
      return;
    }

    let isMounted = true;

    const fetchLogo = async () => {
      const storage = await getFirebaseStorage();
      if (!storage) {
        downloadUrlCache.set(cacheKey, null);
        if (isMounted) {
          setResolvedUrl(null);
        }
        return;
      }

      try {
        const path = buildClientLogoStoragePath(resolvedClientId, variant);
        const downloadUrl = await getDownloadURL(ref(storage, path));
        if (!isMounted) {
          return;
        }
        downloadUrlCache.set(cacheKey, downloadUrl);
        setResolvedUrl(downloadUrl);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        console.warn("Não foi possível recuperar a logo do cliente:", error);
        downloadUrlCache.set(cacheKey, null);
        setResolvedUrl(null);
      }
    };

    fetchLogo();

    return () => {
      isMounted = false;
    };
  }, [resolvedClientId, variant]);

  return resolvedUrl;
};

export const ClientLogo = forwardRef<HTMLDivElement, ClientLogoProps>(
  ({ client = null, clientId = null, logoUrl: _logoUrl, variant, className }, ref) => {
    const [hasImageError, setHasImageError] = useState(false);

    const targetVariant = normalizeVariant(variant);

    const resolvedLogoUrl = useClientLogoSource({
      client,
      clientId,
      variant: targetVariant,
    });

    const clientName = client?.name ?? null;

    const initials = useMemo(() => getClientInitials(clientName), [clientName]);

    useEffect(() => {
      setHasImageError(false);
    }, [resolvedLogoUrl]);

    const shouldRenderImage = Boolean(resolvedLogoUrl && !hasImageError);

    return (
      <div
        ref={ref}
        className={clsx(
          "flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-primary-100",
          className
        )}
      >
        {shouldRenderImage ? (
          <img
            className="h-full w-full rounded-md object-contain p-1"
            src={resolvedLogoUrl ?? undefined}
            alt={clientName ? `Logo ${clientName}` : "Logo do cliente"}
            onError={() => setHasImageError(true)}
          />
        ) : (
          <span className="text-sm font-montserrat font-semibold uppercase text-primary-500">
            {initials}
          </span>
        )}
      </div>
    );
  }
);

ClientLogo.displayName = "ClientLogo";

export default ClientLogo;
